import { useMemo, useRef, useState } from "react";

import type { AeroLiftingLineSolveResponse, AeroSectionSolveRow } from "../../api/aeroWorkbench";
import { CpPlot } from "../highlift/foil/CpPlot";
import { FlowVisLayer } from "../highlift/foil/FlowVisLayer";
import { naca4 } from "../highlift/geometry";
import { createWebGpuSolver, type WebGpuSolveResult } from "../highlift/webgpu-solver/backend";
import { LiftDistributionPlot } from "./LiftDistributionPlot";
import { nearestExactCampaignSection, solverNativeCp } from "./sectionFieldEvidence";

export type SectionAnalysisMode = "wake" | "section-flow" | "cp" | "lift";

type DiagnosticState = "idle" | "checking" | "solving" | "done" | "warning" | "unavailable" | "error";

function nacaCode(airfoil: string): string {
  return airfoil.match(/(\d{4})/)?.[1] ?? "0012";
}
export function SectionAnalysisPanel({
  mode,
  airfoil,
  chordM,
  alphaDeg,
  speedMps,
  wakeResult,
  sectionRows = [],
}: {
  mode: SectionAnalysisMode;
  airfoil: string;
  chordM: number;
  alphaDeg: number;
  speedMps: number;
  wakeResult: AeroLiftingLineSolveResponse | null;
  sectionRows?: readonly AeroSectionSolveRow[];
}) {
  const [state, setState] = useState<DiagnosticState>("idle");
  const [message, setMessage] = useState("Run an independent in-browser WebGPU 2D RANS diagnostic. It does not reuse the campaign solver state.");
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<WebGpuSolveResult | null>(null);
  const [solvedKey, setSolvedKey] = useState<string | null>(null);
  const generationRef = useRef(0);
  const code = nacaCode(airfoil);
  const reynolds = Math.max(50_000, speedMps * chordM / 1.48e-5);
  const mach = Math.max(0, speedMps / 340.3);
  const inputKey = `${code}:${alphaDeg.toFixed(4)}:${reynolds.toFixed(0)}:${mach.toFixed(5)}`;
  const stale = result != null && solvedKey !== inputKey;
  const surfaceCp = result?.flow.surfaceCp ?? [];
  const campaignMatch = useMemo(() => nearestExactCampaignSection(sectionRows, {
    airfoil,
    alphaDeg,
    reynolds,
    mach,
  }), [airfoil, alphaDeg, mach, reynolds, sectionRows]);
  const candidateCampaignCp = useMemo(
    () => solverNativeCp(campaignMatch?.row),
    [campaignMatch],
  );
  const campaignCp = campaignMatch?.samePoint ? candidateCampaignCp : [];
  const displayedCp = result ? surfaceCp : campaignCp;
  const displayedCpSource = result
    ? surfaceCp.length ? "independent browser WebGPU solve" : null
    : campaignCp.length ? "campaign section solver" : null;
  const campaignBackend = campaignMatch?.row.prediction_receipt?.solver_backend
    ?? campaignMatch?.row.solver_config?.backend;
  const campaignPrediction = campaignMatch?.row.prediction;
  const campaignCl = typeof campaignPrediction?.cl === "number" ? campaignPrediction.cl : null;
  const campaignCd = typeof campaignPrediction?.cd === "number" ? campaignPrediction.cd : null;
  const webGpuConverged = result?.diagnostics?.converged === true;
  const webGpuDiagnosticSummary = result?.diagnostics
    ? [
        result.diagnostics.converged === true ? "converged" : result.diagnostics.converged === false ? "not converged" : "convergence not reported",
        result.diagnostics.iterations == null ? null : `${Math.round(result.diagnostics.iterations)} iterations`,
        result.diagnostics.residual == null ? null : `residual ${result.diagnostics.residual.toExponential(2)}`,
      ].filter((value): value is string => value != null).join(" · ")
    : "convergence diagnostics unavailable";
  const contour = useMemo(() => naca4(code, 0, 160), [code]);

  const solve = async () => {
    const generation = ++generationRef.current;
    setState("checking");
    setMessage("Checking this browser's WebGPU adapter…");
    setProgress(0);
    try {
      const solver = createWebGpuSolver();
      if (!(await solver.available())) {
        if (generation !== generationRef.current) return;
        setState("unavailable");
        setMessage("WebGPU is unavailable in this browser. The aircraft section model and relaxed wake remain usable.");
        return;
      }
      setState("solving");
      setMessage("Meshing and solving the selected section locally…");
      const next = await solver.solve(
        {
          elements: [{ name: "section", contour }],
          disks: [],
          flow: { mach, reynolds, temperature: 288.15 },
          alphaDeg,
          maxSteps: 2500,
        },
        (update) => {
          if (generation !== generationRef.current) return;
          setProgress(update.percentage);
          setMessage(update.stage === "extracting" ? "Extracting field and surface pressure…" : `${update.stage ?? "solving"}…`);
        },
      );
      if (generation !== generationRef.current) return;
      setResult(next);
      setSolvedKey(inputKey);
      setProgress(100);
      const converged = next.diagnostics?.converged === true;
      setState(converged ? "done" : "warning");
      setMessage(converged
        ? next.flow.surfaceCp?.length
          ? "Velocity mesh and surface Cp came directly from the independent WebGPU solve—not from the campaign XFOIL/RustFoil row."
          : "Velocity mesh came from the independent WebGPU solve; this result did not include surface Cp samples."
        : "The WebGPU kernel returned a field without native convergence. It remains visible as a diagnostic, but its coefficients are excluded from the campaign cross-check.");
    } catch (error) {
      if (generation !== generationRef.current) return;
      setState("error");
      setMessage(error instanceof Error ? error.message : "WebGPU section solve failed.");
    }
  };

  return (
    <section
      className={`section-analysis-panel${mode === "wake" ? " is-closed" : ""}`}
      aria-hidden={mode === "wake"}
      data-testid="section-analysis-panel"
      data-analysis-mode={mode}
    >
      {mode === "lift" ? (
        wakeResult ? (
          <LiftDistributionPlot response={wakeResult} />
        ) : (
          <div className="section-analysis-empty">
            <strong>Lift distribution follows the relaxed wake</strong>
            <span>Run Quick 3D after the aircraft section model is ready. The latest relaxed-wake panel loads will appear here.</span>
          </div>
        )
      ) : mode === "section-flow" || mode === "cp" ? (
        <>
          <header className="section-diagnostic-header">
            <span>
              <strong>{mode === "section-flow" ? "2D section flow" : "Surface pressure Cp"}</strong>
              <small>{airfoil} · α {alphaDeg.toFixed(1)}° · Re {reynolds.toExponential(2)} · M {mach.toFixed(3)}</small>
            </span>
            <dl>
              <div><dt>C<sub>L</sub></dt><dd>{result ? result.CL.toFixed(3) : "—"}</dd></div>
              <div><dt>C<sub>D</sub></dt><dd>{result ? result.CD.toFixed(4) : "—"}</dd></div>
            </dl>
            <button type="button" onClick={solve} disabled={state === "checking" || state === "solving"}>
              {state === "checking" ? "Checking…" : state === "solving" ? `${Math.round(progress)}%` : result ? "Re-solve section" : "Solve section"}
            </button>
          </header>
          <div className="section-diagnostic-body">
            <div className="section-field-visual">
              {mode === "section-flow" ? (
                <FlowVisLayer flow={result?.flow ?? null} view={null} />
              ) : displayedCp.length ? (
                <CpPlot surfaceCp={displayedCp} width={760} height={250} />
              ) : (
                <div className="section-analysis-empty">
                  <strong>{result ? "No surface Cp in this solve" : "No solver-native surface Cp yet"}</strong>
                  <span>The V100 campaign currently supplies exact coefficients, not a volume field. Run the independent WebGPU diagnostic for a native field/Cp result; no curve is fabricated.</span>
                </div>
              )}
            </div>
            <div className="section-field-evidence" data-testid="section-field-evidence">
              <article>
                <span>Campaign observation</span>
                <strong>{campaignMatch ? `${String(campaignBackend ?? "section solver")} · exact coefficients` : "No exact row sampled yet"}</strong>
                <small>{campaignMatch
                  ? `${campaignMatch.samePoint ? "same point" : "nearest sampled point"} · Cp ${campaignCp.length ? "solver-native" : candidateCampaignCp.length ? "available only at that different point" : "not returned"} · velocity field not returned`
                  : "Run section rows to establish solver evidence."}</small>
              </article>
              <article>
                <span>Displayed 2D field</span>
                <strong>{result ? "Independent WebGPU 2D RANS solve" : displayedCpSource ? `Solver-native Cp · ${displayedCpSource}` : "No field displayed · independent WebGPU 2D RANS available"}</strong>
                <small>{result
                  ? `Native velocity samples · ${webGpuDiagnosticSummary}; not reconstructed from Cl/Cd and not campaign training evidence.`
                  : "Campaign coefficients alone cannot reconstruct a unique flow field."}</small>
              </article>
              <article>
                <span>3D assembly</span>
                <strong>Section NN → lifting line → relaxed wake</strong>
                <small>Span loads and wake are reduced-order solver outputs, not stacked 2D CFD or a full 3D volume field.</small>
              </article>
            </div>
            {result && webGpuConverged && !stale && campaignMatch && campaignCl != null && campaignCd != null ? (
              <div className="section-coefficient-comparison" data-testid="section-coefficient-comparison">
                <span>Independent cross-check{campaignMatch.samePoint ? " · same point" : " · nearest campaign point"}</span>
                <b>ΔC<sub>L</sub> {(result.CL - campaignCl).toFixed(3)}</b>
                <b>ΔC<sub>D</sub> {(result.CD - campaignCd).toFixed(4)}</b>
                <small>{campaignMatch.samePoint
                  ? "WebGPU minus exact campaign observation"
                  : `condition offsets: Δα ${campaignMatch.alphaDeltaDeg.toFixed(2)}° · Re ×${campaignMatch.reynoldsRatio.toFixed(2)} · ΔM ${campaignMatch.machDelta.toFixed(3)}; comparison is indicative only`}</small>
              </div>
            ) : null}
          </div>
          <footer className={`section-diagnostic-status is-${state}`} role={state === "error" ? "alert" : "status"}>
            <span>{state === "idle" && campaignCp.length && mode === "cp"
              ? "Showing solver-native surface Cp from the nearest exact campaign observation."
              : message}</span>
            {stale
              ? <em>Inputs changed · displayed result is stale</em>
              : <em>{displayedCpSource === "campaign section solver" && !result ? "campaign solver-native Cp" : "independent diagnostic · not aircraft-model evidence"}</em>}
          </footer>
        </>
      ) : null}
    </section>
  );
}
